import { css } from '@styled-system/css';
import {
  CategoryScale,
  Chart as ChartJS,
  type ChartData,
  type ChartOptions,
  Filler,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js';
import { type FC } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import { useTheme } from '@/app/providers/ThemeProvider';
import { Spinner } from '@/shared/components/ui/Spinner';
import { dsTokens } from '@/shared/theme/tokens';

import { useVeilleLiveCurve } from '../useVeilleApi';
import type { VeilleView } from '../veilleTypes';
import { liveDot } from './veilleUi';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

// ============================================================================
// Évolution EN DIRECT (getVeilleLiveCurve, contrat 2026-08-14) : 41 points
// (11 x 30 min puis 30 x 1 min, bornes calées sur la minute courante — les
// heures « bancales » sont normales). On TRACE les débits par minute
// (salesPerMinute/newItemsPerMinute, continus malgré le changement de largeur
// de tranche) et on montre les COMPTES BRUTS au survol. Sondage ~5 s, erreur
// = transitoire. La DERNIÈRE minute est EN COURS : pointillé (elle lit bas).
// ============================================================================

const card = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '4',
  paddingY: '5',
  paddingX: '6',
  bg: 'surface.subtle',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  borderRadius: 'lg',
});

const header = css({ display: 'flex', alignItems: 'center', gap: '3', flexWrap: 'wrap' });

const title = css({ fontSize: 'bodySm', fontWeight: 'semibold', color: 'text.primary' });

const legendItem = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2',
  fontSize: 'caption',
  color: 'text.tertiary',
});

const legendSwatch = css({ width: '8px', height: '8px', borderRadius: 'full', flexShrink: 0 });

export const VeilleLiveChart: FC<{ veille: VeilleView }> = ({ veille }) => {
  const { t, i18n } = useTranslation();
  const { theme: colorMode } = useTheme();
  const ds = dsTokens[colorMode];
  const locale = i18n.language?.startsWith('en') ? 'en-GB' : 'fr-FR';

  const { points, isLoading } = useVeilleLiveCurve(veille.id, true);

  const labels = points.map((point) =>
    new Date(point.bucketStart).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }),
  );

  // La dernière tranche est EN COURS (bornée à l'instant de lecture) : son
  // segment est tracé en pointillé pour ne pas la lire comme une chute
  const lastSegmentIndex = points.length - 1;
  const seriesFor = (label: string, values: number[], color: string) => ({
    label,
    data: values,
    borderColor: color,
    backgroundColor: `${color}1f`,
    fill: true,
    tension: 0.35,
    pointRadius: 2,
    pointHoverRadius: 4,
    pointBackgroundColor: color,
    pointBorderColor: ds['surface.default'],
    borderWidth: 2,
    segment: {
      borderDash: (context: { p1DataIndex: number }) =>
        context.p1DataIndex === lastSegmentIndex ? [5, 4] : undefined,
    },
  });

  const data: ChartData<'line'> = {
    labels,
    datasets: [
      seriesFor(t('veille.kpi.sales'), points.map((point) => point.salesPerMinute), ds['brand.primary']),
      seriesFor(t('veille.liveAdds'), points.map((point) => point.newItemsPerMinute), ds['warning.default']),
    ],
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 250 },
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: ds['surface.elevated'],
        titleColor: ds['text.primary'],
        bodyColor: ds['text.secondary'],
        borderColor: ds['border.default'],
        borderWidth: 1,
        callbacks: {
          // Le survol montre les COMPTES BRUTS de la tranche (jamais arrondis
          // côté données), le débit /min étant la série tracée
          label: (context) => {
            const point = points[context.dataIndex];
            if (!point) return '';
            const isSales = context.datasetIndex === 0;
            const raw = isSales ? point.sales : point.newItems;
            const rate = isSales ? point.salesPerMinute : point.newItemsPerMinute;
            const label = isSales ? t('veille.kpi.sales') : t('veille.liveAdds');
            return `${label} : ${raw} (${rate.toLocaleString(locale, { maximumFractionDigits: 1 })}/min)`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: ds['text.tertiary'], font: { size: 10 }, maxTicksLimit: 12 },
        border: { color: ds['border.subtle'] },
      },
      y: {
        grid: { color: ds['border.subtle'] },
        ticks: { color: ds['text.tertiary'], font: { size: 10 }, maxTicksLimit: 5 },
        border: { display: false },
        beginAtZero: true,
      },
    },
  };

  return (
    <div className={card}>
      <div className={header}>
        {veille.state !== 'paused' && <span className={liveDot} />}
        <span className={title}>{t('veille.liveChartTitle')}</span>
        <span className={css({ display: 'inline-flex', gap: '5', marginLeft: 'auto' })}>
          <span className={legendItem}>
            <span className={legendSwatch} style={{ background: ds['brand.primary'] }} />
            {t('veille.kpi.sales')} /min
          </span>
          <span className={legendItem}>
            <span className={legendSwatch} style={{ background: ds['warning.default'] }} />
            {t('veille.liveAdds')} /min
          </span>
        </span>
      </div>
      <div style={{ height: 200 }} className={css({ position: 'relative' })}>
        {isLoading && points.length === 0 ? (
          <div
            className={css({
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '3',
              height: '100%',
              textAlign: 'center',
            })}
          >
            <Spinner size={18} />
            <span className={css({ fontSize: 'caption', color: 'text.tertiary', maxWidth: '380px', lineHeight: 1.6 })}>
              {t('veille.curveLoadingHint')}
            </span>
          </div>
        ) : (
          <Line data={data} options={options} />
        )}
      </div>
    </div>
  );
};
